// src/hooks/results/useSpirometryResults.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { documentId } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../utils/results/recordingIdTime";
import {
  getInitialRealtimeListenLimit,
  getNextRealtimeListenLimit,
  selectRealtimeMeasurementDocs,
  shouldExpandRealtimeListenLimit,
} from "./loadStagedMeasurementDocs";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../utils/results/spirometry";
import { buildSnapshotSubset } from "./readSnapshotField";

const SPIROMETRY_LIST_FIELDS = [
  "Signal_Quality",
  "signal_quality",
  "results.mpm.rr_avg",
  "results.mpm.rr_mean",
  "results.mpm.rr_median",
  "results.mpm.rr_min",
  "results.mpm.rr_max",
  "results.mpm.respiratory_rate",
  "results.mpm.flow_loop.quality_status",
  "results.mpm.flow_loop.quality",
  "results.mpm.spirometry",
  "results.mpm_signal_quality",
  "results.mpm_spiro_signal_quality",
  "results.mpm.mpm_signal_quality",
  "results.mpm.mpm_spiro_signal_quality",
];

export function useSpirometryResults(
  db,
  uid,
  {
    pageSize = 50,
    predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
  } = {},
) {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!!db && !!uid);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedSnapshotRef = useRef(false);
  const pageSizeRef = useRef(pageSize);

  const colRef = useMemo(() => {
    if (!db || !uid) return null;
    return collection(db, "measurements", uid, "RecordingSpirometry");
  }, [db, uid]);

  const initialListenLimit = useMemo(
    () => getInitialRealtimeListenLimit(pageSize, false),
    [pageSize],
  );
  const [listenLimit, setListenLimit] = useState(initialListenLimit);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    hasLoadedSnapshotRef.current = false;
    setListenLimit(getInitialRealtimeListenLimit(pageSizeRef.current, false));
    setLoading(!!colRef);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
  }, [colRef]);

  useEffect(() => {
    setListenLimit((currentLimit) =>
      Math.max(currentLimit, getInitialRealtimeListenLimit(pageSize, false)),
    );

    if (hasLoadedSnapshotRef.current) {
      setLoadingMore(true);
    }
  }, [pageSize]);

  useEffect(() => {
    if (!colRef) {
      setRows([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
      return;
    }

    let cancelled = false;

    if (!hasLoadedSnapshotRef.current) {
      setLoading(true);
      setLoadingMore(false);
    }
    setError(null);

    const baseConstraints = [orderBy(documentId(), "desc")];

    const mapDocsToRows = (docs) =>
      docs.map((doc) => {
        const data = buildSnapshotSubset(doc, SPIROMETRY_LIST_FIELDS);
        const ts = parseTimestampFromRecordingId(doc.id);
        const { time, date } = formatRecordingIdDateParts(
          doc.id,
          i18n.resolvedLanguage || i18n.language || "en-GB",
        );
        const {
          fev1,
          fvc,
          fev1FvcRatio,
          pef,
          rr,
          signalQuality,
          spirometryQuality,
        } = pickSpirometryResult(data, { predictionSource });

        return {
          id: doc.id,
          timestamp: ts ? ts.toISOString() : null,
          time,
          date,
          fev1,
          fvc,
          fev1FvcRatio,
          pef,
          rr: rr?.average ?? rr?.mean ?? null,
          signalQuality,
          spirometryQuality,
        };
      });

    const qy = query(colRef, ...baseConstraints, limit(listenLimit));

    const unsub = onSnapshot(
      qy,
      { includeMetadataChanges: true },
      (snap) => {
        if (cancelled) return;

        const docs = selectRealtimeMeasurementDocs(snap.docs, pageSize);

        setRows(mapDocsToRows(docs));
        hasLoadedSnapshotRef.current = true;
        setLoading(false);
        setHasMore(snap.size >= listenLimit);

        const needsExpansion = shouldExpandRealtimeListenLimit(
          snap.size,
          listenLimit,
          docs.length,
          pageSize,
        );

        setLoadingMore(needsExpansion);

        if (needsExpansion) {
          setListenLimit((currentLimit) =>
            getNextRealtimeListenLimit(currentLimit, pageSize, false),
          );
        }
      },
      (err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [
    colRef,
    pageSize,
    listenLimit,
    i18n.resolvedLanguage,
    i18n.language,
    predictionSource,
  ]);

  return { rows, loading, loadingMore, hasMore, error };
}
